import React from 'react';
import { observer } from 'mobx-react';
import { Subtitle1 } from '@estee/elc-typography';
import {
    SpinnerSelectorWrapper,
    ActionButton,
    MinusIconStyled,
    ContentDataWrapper,
    PlusIconStyled,
    StyledTooltip
} from './SpinnerSelectorStyles';
import { classes } from '~utils/ClassNames';
import { useTranslation } from '~sharedComponents/Providers/Translations/TranslationsHook';
import { fillTranslation } from '~sharedComponents/Providers/Translations/utils/fillTranslation';

export interface ISpinnerSelector {
    quantity: number;
    maxQty: number;
    isCartQtyLimitExceeded: boolean;
    isCartQtyLimitReached: boolean;
    disableDecrease: boolean;
    disableIncrease: boolean;
    onRangeDecrease(): void;
    onRangeIncrease(): void;
    className?: string;
    disableActions?: boolean;
}

export const spinnerBaseClass = 'spinner-selector';
export const dataClass = classes(`${spinnerBaseClass}-data`);
const buttonClass = classes(`${spinnerBaseClass}-button`);
const decreaseButton = `${buttonClass}-back`;
const increaseButton = `${buttonClass}-forward`;
const dataWrapperClass = classes(`${spinnerBaseClass}-data-wrapper`);
const minusIconClass = classes(`${spinnerBaseClass}-minus-icon`);
const plusIconClass = classes(`${spinnerBaseClass}-plus-icon`);

const translations = ['itemLimitedToPerCustomer', 'maximumNumberOfItemsInBag', 'increase_product_quantity', 'decrease_product_quantity'];

export const SpinnerSelector = observer((props: ISpinnerSelector) => {
    const {
        className,
        quantity,
        maxQty,
        isCartQtyLimitExceeded,
        isCartQtyLimitReached,
        disableActions,
        disableDecrease,
        disableIncrease,
        onRangeDecrease,
        onRangeIncrease
    } = props;

    const { itemLimitedToPerCustomer, maximumNumberOfItemsInBag, increase_product_quantity, decrease_product_quantity } = useTranslation(translations);
    const maxItemQuantityText = fillTranslation(
        itemLimitedToPerCustomer,
        maxQty
    );

    const spinnerSelectorClass = classes(spinnerBaseClass, className);
    const disableDecreaseBtn = disableDecrease || disableActions;
    const disableIncreaseBtn = disableIncrease || disableActions;

    const showMaxItemQuantityText = quantity >= maxQty;
    const maximumLimitText = isCartQtyLimitReached ? maximumNumberOfItemsInBag : maxItemQuantityText;
    const showMaximumLimitText = (showMaxItemQuantityText || isCartQtyLimitReached) && !isCartQtyLimitExceeded;

    return (
        <SpinnerSelectorWrapper className={spinnerSelectorClass} data-testid={spinnerSelectorClass}>
            <ActionButton
                className={decreaseButton}
                onClick={onRangeDecrease}
                disabled={disableDecreaseBtn}
                data-testid="minus_spinner_selector"
                value="minus_spinner_selector"
                aria-label={decrease_product_quantity}
            >
                <MinusIconStyled className={minusIconClass} disabled={disableDecreaseBtn} />
            </ActionButton>
            <ContentDataWrapper aria-live="polite" tabIndex={0} className={dataWrapperClass}>
                <Subtitle1 className={dataClass} data-testid={dataClass}>
                    {quantity}
                </Subtitle1>
            </ContentDataWrapper>
            {showMaximumLimitText ? (
                <StyledTooltip hiddenText={maximumLimitText} placement="bottom" type="alert" className={`${increaseButton} disabled`}>
                    <PlusIconStyled className={plusIconClass} />
                </StyledTooltip>
            ) : (
                <ActionButton
                    className={increaseButton}
                    onClick={onRangeIncrease}
                    disabled={disableIncreaseBtn}
                    data-testid="plus_spinner_selector"
                    value="plus_spinner_selector"
                    aria-label={increase_product_quantity}
                >
                    <PlusIconStyled className={plusIconClass} disabled={disableIncreaseBtn} />
                </ActionButton>
            )}
        </SpinnerSelectorWrapper>
    );
});
